import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, LocateFixed, MapPin, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import MapGL, { Marker, Source, Layer } from 'react-map-gl';
import type { LayerProps } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PhoneNumberDisplay from '@/components/ui/PhoneNumberDisplay';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import { attachCountryCodeToPhone, parsePhoneNumber } from '@/utils';
import { PickupTypeEnum } from '@/types';
import { MAPBOX_TOKEN } from '@/hooks/useMapCoordinates';
import {
  BAGHDAD_BOUNDS,
  BAGHDAD_CENTER,
  BAGHDAD_MASK_GEOJSON,
  BAGHDAD_BORDER_GEOJSON,
  isInsideBaghdad,
} from '@/utils/helpers/mapGeofence';

/* ── Map overlay layers ── */
const maskLayer: LayerProps = {
  id: 'baghdad-mask',
  type: 'fill',
  paint: {
    'fill-color': '#000000',
    'fill-opacity': 0.35,
  },
};

const borderLayer: LayerProps = {
  id: 'baghdad-border',
  type: 'line',
  paint: {
    'line-color': '#3b82f6',
    'line-width': 2.5,
    'line-opacity': 0.7,
    'line-dasharray': [3, 2],
  },
};

const borderGlowLayer: LayerProps = {
  id: 'baghdad-border-glow',
  type: 'line',
  paint: {
    'line-color': '#3b82f6',
    'line-width': 8,
    'line-opacity': 0.15,
    'line-blur': 6,
  },
};

/* ── Map picker ── */
interface MapPickerProps {
  lat: number;
  lng: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const MapPicker = ({ lat, lng, onLocationChange }: MapPickerProps) => {
  const { t } = useTranslation();
  const [viewport, setViewport] = useState({ latitude: lat, longitude: lng, zoom: 12 });
  const [outsideWarning, setOutsideWarning] = useState(false);
  const [pinValid, setPinValid] = useState(true);

  useEffect(() => {
    setViewport((v) => ({ ...v, latitude: lat, longitude: lng }));
    setPinValid(isInsideBaghdad(lat, lng));
  }, [lat, lng]);

  const handleMapClick = useCallback(
    (e: mapboxgl.MapLayerMouseEvent) => {
      const clickLat = e.lngLat.lat;
      const clickLng = e.lngLat.lng;

      if (isInsideBaghdad(clickLat, clickLng)) {
        setOutsideWarning(false);
        setPinValid(true);
        onLocationChange(clickLat, clickLng);
      } else {
        setOutsideWarning(true);
        setPinValid(false);
        // Auto-dismiss warning after 3s
        setTimeout(() => setOutsideWarning(false), 3000);
      }
    },
    [onLocationChange],
  );

  const handleDragEnd = useCallback(
    (e: { lngLat: { lat: number; lng: number } }) => {
      const dragLat = e.lngLat.lat;
      const dragLng = e.lngLat.lng;

      if (isInsideBaghdad(dragLat, dragLng)) {
        setOutsideWarning(false);
        setPinValid(true);
        onLocationChange(dragLat, dragLng);
      } else {
        setOutsideWarning(true);
        setPinValid(false);
        // Snap back to previous valid position
        setTimeout(() => {
          setOutsideWarning(false);
        }, 3000);
      }
    },
    [onLocationChange],
  );

  const handleRecenter = () => {
    setViewport((v) => ({
      ...v,
      latitude: BAGHDAD_CENTER.lat,
      longitude: BAGHDAD_CENTER.lng,
      zoom: 11,
    }));
  };

  return (
    <div className='relative'>
      <MapGL
        {...viewport}
        style={{ width: '100%', height: 320, borderRadius: 12 }}
        mapStyle='mapbox://styles/mapbox/streets-v11'
        mapboxAccessToken={MAPBOX_TOKEN}
        maxBounds={BAGHDAD_BOUNDS}
        onMove={(e) => setViewport(e.viewState)}
        onClick={handleMapClick}
      >
        {/* Dark mask outside Baghdad */}
        <Source id='baghdad-mask' type='geojson' data={BAGHDAD_MASK_GEOJSON}>
          <Layer {...maskLayer} />
        </Source>

        {/* Border glow + dashed line */}
        <Source id='baghdad-border' type='geojson' data={BAGHDAD_BORDER_GEOJSON}>
          <Layer {...borderGlowLayer} />
          <Layer {...borderLayer} />
        </Source>

        {/* Pin marker */}
        <Marker latitude={lat} longitude={lng} draggable onDragEnd={handleDragEnd}>
          <div
            className={`transition-all duration-300 ${pinValid ? 'scale-100' : 'scale-90 opacity-60'}`}
          >
            <MapPin
              className={`h-8 w-8 drop-shadow-lg ${pinValid ? 'text-sd-primary' : 'text-red-500'}`}
              fill={pinValid ? 'currentColor' : 'transparent'}
              strokeWidth={2}
            />
          </div>
        </Marker>
      </MapGL>

      {/* Recenter button — glassmorphic */}
      <button
        type='button'
        onClick={handleRecenter}
        className='absolute top-3 right-3 z-10 p-2 rounded-lg bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm hover:bg-white/95 transition-all duration-200 group'
        title={String(t('recenter_map'))}
      >
        <LocateFixed className='h-4 w-4 text-sd-muted-foreground group-hover:text-sd-foreground transition-colors' />
      </button>

      {/* Outside service area warning — slides in */}
      <div
        className={`absolute bottom-3 left-3 right-3 z-10 transition-all duration-300 ${
          outsideWarning
            ? 'translate-y-0 opacity-100'
            : 'translate-y-2 opacity-0 pointer-events-none'
        }`}
      >
        <div className='flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/90 backdrop-blur-sm text-white text-xs font-medium shadow-lg'>
          <ShieldAlert className='h-4 w-4 shrink-0' />
          <span>{t('location_outside_service_area')}</span>
        </div>
      </div>

      {/* Valid location indicator — subtle */}
      {pinValid && !outsideWarning && (
        <div className='absolute bottom-3 left-3 z-10'>
          <div className='flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm border border-white/50 shadow-sm text-xs text-emerald-600 font-medium'>
            <CheckCircle2 className='h-3.5 w-3.5' />
            <span>{t('inside_service_area')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Form state ── */
export interface LocationFormState {
  full_name: string;
  countryCode: string;
  phone: string;
  title: string;
  type: string;
  province: string;
  region_uid: string;
  address_text: string;
  lat: number;
  lng: number;
}

export const EMPTY_LOCATION_FORM: LocationFormState = {
  full_name: '',
  countryCode: '964',
  phone: '',
  title: '',
  type: PickupTypeEnum.WAREHOUSE,
  province: '',
  region_uid: '',
  address_text: '',
  lat: 33.3152,
  lng: 44.3661,
};

export interface LocationFormPayload {
  full_name: string;
  phone: string;
  title: string;
  type: string;
  region_uid: string;
  address_text: string;
  lat: string;
  lng: string;
  default?: boolean;
}

export const buildPayload = (
  form: LocationFormState,
  setAsDefault?: boolean,
): LocationFormPayload => ({
  full_name: form.full_name,
  phone: attachCountryCodeToPhone(form.phone, form.countryCode) ?? '',
  title: form.title,
  type: form.type,
  region_uid: form.region_uid,
  address_text: form.address_text,
  lat: String(form.lat),
  lng: String(form.lng),
  ...(setAsDefault !== undefined ? { default: setAsDefault } : {}),
});

export const prefillFromApi = (
  obj: {
    full_name: string;
    phone: string;
    title: string;
    type: string;
    address_text: string;
    lat: string;
    lng: string;
    region?: { uid?: string; province_code?: string };
  },
  parsePhone: typeof parsePhoneNumber,
): LocationFormState => {
  const phoneInfo = parsePhone(obj.phone);
  const provinceCode = obj.region?.province_code ?? '';
  return {
    full_name: obj.full_name ?? '',
    countryCode: phoneInfo.countryCode ?? '964',
    phone: phoneInfo.phone ?? '',
    title: obj.title ?? '',
    type: obj.type ?? PickupTypeEnum.WAREHOUSE,
    province: provinceCode,
    region_uid: obj.region?.uid ?? '',
    address_text: obj.address_text ?? '',
    lat: obj.lat ? +obj.lat : 33.3152,
    lng: obj.lng ? +obj.lng : 44.3661,
  };
};

/* ── Props ── */
interface LocationFormProps {
  heading: string;
  initialForm?: LocationFormState;
  isSaving: boolean;
  onSave: (form: LocationFormState) => void;
  isEditMode?: boolean;
}

/* ── Component ── */
const LocationForm = ({
  heading,
  initialForm,
  isSaving,
  onSave,
  isEditMode = false,
}: LocationFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<LocationFormState>(initialForm ?? EMPTY_LOCATION_FORM);
  const [selectedProvince, setSelectedProvince] = useState(initialForm?.province ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  /* Sync when initialForm arrives (edit prefill) */
  useEffect(() => {
    if (initialForm) {
      setForm(initialForm);
      setSelectedProvince(initialForm.province);
    }
  }, [initialForm]);

  const { data: provinces, isLoading: isLoadingProvinces } = useFetchListProvinces();
  const { data: areas, isInitialLoading: isLoadingAreas } =
    useFetchAreaByProvinceCode(selectedProvince);

  /* Auto-select Baghdad province when provinces load (for new locations) */
  useEffect(() => {
    if (!isEditMode && provinces?.object?.length && !form.province) {
      const baghdad = provinces.object.find(
        (p) => p.province.toLowerCase().includes('baghdad') || p.province.includes('بغداد'),
      );
      if (baghdad) {
        setSelectedProvince(baghdad.province_code);
        setForm((f) => ({ ...f, province: baghdad.province_code }));
      }
    }
  }, [provinces, isEditMode, form.province]);

  const inputClass = (field?: string) =>
    `w-full h-10 px-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
      field && fieldErrors[field] ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'
    }`;

  const handleSave = () => {
    const errors: Record<string, boolean> = {};
    if (!form.title.trim()) errors.location_name = true;
    if (!form.full_name.trim()) errors.name = true;
    if (!form.phone.trim()) errors.phone_number = true;
    if (!form.province.trim()) errors.province = true;
    if (!form.region_uid.trim()) errors.area = true;
    if (!form.address_text.trim()) errors.address = true;

    // Geofence validation — pin must be inside Baghdad
    if (!isInsideBaghdad(form.lat, form.lng)) {
      errors.map_location = true;
      toast({
        title: String(t('location_outside_service_area')),
        description: String(t('location_outside_service_area_desc')),
        variant: 'destructive',
      });
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      if (!errors.map_location) {
        const missing = Object.keys(errors).map(k => String(t(k))).join(', ');
        toast({ title: t('required_fields_missing'), description: missing, variant: 'destructive' });
      }
      return;
    }
    onSave(form);
  };

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        {/* Header */}
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={() => navigate('/settings/locations')}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground m-0'>{heading}</h2>
          </div>

          <div className='p-6 space-y-6'>
            {/* Address Details */}
            <div>
              <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
                {t('address_details')}
              </p>
              <div className='space-y-4'>
                {/* Location name */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('location_name')} *
                  </label>
                  <input
                    type='text'
                    value={form.title}
                    onChange={(e) => { setForm((f) => ({ ...f, title: e.target.value })); setFieldErrors(prev => ({ ...prev, location_name: false })); }}
                    placeholder={t('location_name')}
                    className={inputClass('location_name')}
                  />
                  {fieldErrors.location_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                </div>

                {/* Type */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('type')} *
                  </label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                  >
                    <SelectTrigger className='h-10 rounded-lg border-sd-input bg-sd-input-bg text-sm text-sd-foreground focus:ring-sd-ring'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PickupTypeEnum.WAREHOUSE}>
                        {t('pickup_address_type.warehouse')}
                      </SelectItem>
                      <SelectItem value={PickupTypeEnum.COD}>
                        {t('pickup_address_type.COD_drop_off_address')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Contact name */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('name')} *
                  </label>
                  <input
                    type='text'
                    value={form.full_name}
                    onChange={(e) => { setForm((f) => ({ ...f, full_name: e.target.value })); setFieldErrors(prev => ({ ...prev, name: false })); }}
                    placeholder={t('name')}
                    className={inputClass('name')}
                  />
                  {fieldErrors.name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                </div>

                {/* Phone */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('phone_number')} *
                  </label>
                  <div className={fieldErrors.phone_number ? 'rounded-lg ring-1 ring-red-500' : ''}>
                    <PhoneNumberDisplay
                      phoneCode={`+${form.countryCode}`}
                      nationalPhone={form.phone}
                      onPhoneCodeChange={(value) =>
                        setForm((f) => ({
                          ...f,
                          countryCode: value.replace(/\D/g, ''),
                        }))
                      }
                      onNationalPhoneChange={(value) => { setForm((f) => ({ ...f, phone: value })); setFieldErrors(prev => ({ ...prev, phone_number: false })); }}
                      placeholder={t('phone_number')}
                    />
                  </div>
                  {fieldErrors.phone_number && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                </div>
              </div>
            </div>

            <div className='border-t border-sd-border' />

            {/* Location Info */}
            <div>
              <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
                {t('location_info')}
              </p>
              <div className='space-y-4'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {/* Province — only Baghdad available */}
                  <div className='space-y-1.5'>
                    <label className='text-xs font-medium text-sd-muted-foreground'>
                      {t('province')} *
                    </label>
                    <Select
                      value={form.province}
                      onValueChange={(v) => {
                        setSelectedProvince(v);
                        setForm((f) => ({ ...f, province: v, region_uid: '' }));
                        setFieldErrors((prev) => ({ ...prev, province: false }));
                      }}
                      disabled={isLoadingProvinces}
                    >
                      <SelectTrigger className={`h-10 rounded-lg text-sm text-sd-foreground focus:ring-sd-ring ${fieldErrors.province ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'}`}>
                        <SelectValue placeholder={t('province')} />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces?.object
                          ?.filter((p) => p.province.toLowerCase().includes('baghdad') || p.province.includes('بغداد'))
                          .map((p) => (
                            <SelectItem key={p.province_code} value={p.province_code}>
                              {p.province}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.province && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                  </div>

                  {/* Area */}
                  <div className='space-y-1.5'>
                    <label className='text-xs font-medium text-sd-muted-foreground'>
                      {t('area')} *
                    </label>
                    <Select
                      value={form.region_uid}
                      onValueChange={(v) => { setForm((f) => ({ ...f, region_uid: v })); setFieldErrors(prev => ({ ...prev, area: false })); }}
                      disabled={!selectedProvince || isLoadingAreas}
                    >
                      <SelectTrigger className={`h-10 rounded-lg text-sm text-sd-foreground focus:ring-sd-ring ${fieldErrors.area ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'}`}>
                        <SelectValue placeholder={t('area')} />
                      </SelectTrigger>
                      <SelectContent>
                        {areas?.object?.map((a) => (
                          <SelectItem key={a.uid} value={a.uid}>
                            {a.area_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.area && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                  </div>
                </div>

                {/* Address line */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('address')} *
                  </label>
                  <input
                    type='text'
                    value={form.address_text}
                    onChange={(e) => { setForm((f) => ({ ...f, address_text: e.target.value })); setFieldErrors(prev => ({ ...prev, address: false })); }}
                    placeholder={t('address')}
                    className={inputClass('address')}
                  />
                  {fieldErrors.address && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
                </div>

                {/* Map */}
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <label className='text-xs font-medium text-sd-muted-foreground'>
                      {t('pin_location_on_map')} *
                    </label>
                    <span className='text-[10px] text-sd-muted-foreground font-medium px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100'>
                      {t('baghdad_service_area')}
                    </span>
                  </div>
                  <p className='text-xs text-sd-muted-foreground'>{t('click_map_or_drag_baghdad')}</p>
                  <MapPicker
                    lat={form.lat}
                    lng={form.lng}
                    onLocationChange={(lat, lng) => {
                      setForm((f) => ({ ...f, lat, lng }));
                      setFieldErrors((prev) => ({ ...prev, map_location: false }));
                    }}
                  />
                  <div className='flex items-center justify-between'>
                    <p className='text-xs text-sd-muted-foreground'>
                      {t('coordinates')}: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                    </p>
                  </div>
                  {fieldErrors.map_location && (
                    <p className='text-xs text-red-500'>{t('location_outside_service_area_desc')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={() => navigate('/settings/locations')}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-card text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60'
            >
              {isSaving ? t('loading') : isEditMode ? t('update_address') : t('save_address')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationForm;
